// Lógica pura de la sección Cables: resultado de la inspección anual, estado
// de los eventos, vida útil, filtros, menciones de la inspección diaria e
// historial de la ficha. Sin red ni React. La pantalla, el estado y el reporte
// consumen esto. Mismo concepto que lib/arboles (de ahí se reutilizan la
// agrupación por año, el plazo en palabras y las medidas).
//
// Tablas (migración 20261015200000_cables_lineas): cables_lineas (ficha),
// cables_linea_eventos (append-only, se anulan con motivo),
// cables_lineas_historial (fila anterior en jsonb) y la vista
// cables_v_lineas_estado (cable + último evento vigente + resultado vigente +
// próxima + vencida + vida útil + menciones de 30 días).
import { normalizarTexto } from './auditoria';
import { instalacionDe, instalacionValida, etiquetaInstalacion } from './auditoriaInstalaciones';
import { compararCodigo, historialFicha } from './arboles';

export const RESULTADOS = ['apto', 'apto_con_observaciones', 'no_apto'];
export const RESULTADO_LABEL = { apto: 'Apto', apto_con_observaciones: 'Con observaciones', no_apto: 'No apto' };
export const RESULTADO_ORDEN = { apto: 1, apto_con_observaciones: 2, no_apto: 3 };

// Punto de estado discreto (color + texto, nunca solo color).
export const RESULTADO_PUNTO = {
  apto: 'bg-success',
  apto_con_observaciones: 'bg-warning',
  no_apto: 'bg-error',
};

// Botones del formulario (con los tokens de estado de VLS).
export const RESULTADO_PILL = {
  apto: 'border bg-success/10 text-success border-success/30',
  apto_con_observaciones: 'border bg-warning/15 text-warning-foreground border-warning/50',
  no_apto: 'border bg-error/10 text-error border-error/40',
};

// Para el PDF (mismos tonos que RIESGO_RGB de árboles).
export const RESULTADO_RGB = {
  apto: [22, 163, 74],
  apto_con_observaciones: [217, 119, 6],
  no_apto: [220, 38, 38],
};

export const TIPOS_EVENTO = [
  { id: 'inspeccion_anual', label: 'Inspección anual' },
  { id: 'revision', label: 'Revisión' },
  { id: 'mantenimiento', label: 'Mantenimiento' },
  { id: 'cambio_cable', label: 'Cambio de cable' },
  { id: 'falla', label: 'Falla' },
  { id: 'otro', label: 'Otro' },
];
export const tipoEventoLabel = (t) => TIPOS_EVENTO.find((x) => x.id === t)?.label || t || 'Sin dato';

export const esResultadoMalo = (r) => r === 'apto_con_observaciones' || r === 'no_apto';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const idLineaValido = (id) => typeof id === 'string' && UUID.test(id);
export const rutaLinea = (id) => `/cables/${id}`;

// "Plataforma 1 → Plataforma 2" (o lo que haya).
export const tramo = (l) => {
  const desde = String(l?.plataforma_desde || '').trim();
  const hasta = String(l?.plataforma_hasta || '').trim();
  if (!desde && !hasta) return '';
  return `${desde || 'Sin dato'} → ${hasta || 'Sin dato'}`;
};

export const nombreLinea = (l) => [l?.codigo, l?.nombre || tramo(l)].filter(Boolean).join(' · ') || 'Cable';

// ── Vida útil ──
const DIA_MS = 86400000;
const redondear1 = (n) => Math.round(n * 10) / 10;

// Años desde la instalación hasta hoy (1 decimal) o null.
export function edadAnios(fechaInstalacion, hoy) {
  if (!/^\d{4}-\d{2}-\d{2}/.test(fechaInstalacion || '') || !/^\d{4}-\d{2}-\d{2}/.test(hoy || '')) return null;
  const d = (Date.parse(`${hoy.slice(0, 10)}T12:00:00Z`) - Date.parse(`${fechaInstalacion.slice(0, 10)}T12:00:00Z`)) / DIA_MS;
  return redondear1(d / 365.25);
}

// { edad, vida, restante, usado (0..1), vencido } con lo que haya.
export function vidaUtil(linea, hoy) {
  const edad = edadAnios(linea?.fecha_instalacion, hoy);
  const vida = Number(linea?.vida_util_anios);
  const tieneVida = linea?.vida_util_anios !== null && linea?.vida_util_anios !== undefined && linea?.vida_util_anios !== ''
    && Number.isFinite(vida) && vida > 0;
  if (edad === null || !tieneVida) {
    return { edad, vida: tieneVida ? vida : null, restante: null, usado: null, vencido: false };
  }
  const restante = redondear1(vida - edad);
  return { edad, vida, restante, usado: Math.min(Math.max(edad / vida, 0), 1), vencido: restante < 0 };
}

// "3,5 años" / "1 año" / "menos de un año".
export function textoAnios(n) {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return null;
  const v = Math.abs(Number(n));
  if (v < 0.1) return 'menos de un mes';
  const t = v.toLocaleString('es-CR', { maximumFractionDigits: 1 });
  return `${t} ${v === 1 ? 'año' : 'años'}`;
}

// ── Eventos ──
const ordenEvento = (a, b) => (b.fecha || '').localeCompare(a.fecha || '')
  || (b.created_at || '').localeCompare(a.created_at || '') || String(b.id).localeCompare(String(a.id));

export const eventosOrdenados = (evs) => [...(evs || [])].sort(ordenEvento);
export const ultimoEvento = (evs) => eventosOrdenados((evs || []).filter((e) => e && !e.anulada))[0] || null;

// Estado derivado de un cable a partir de sus eventos (mismo cálculo que la
// vista): el resultado sale del último evento vigente que lo tiene.
export function estadoDesdeEventos(evs, linea, hoy) {
  const ultimo = ultimoEvento(evs);
  const conResultado = ultimoEvento((evs || []).filter((e) => e && e.resultado));
  const proxima = ultimo?.proxima_fecha || null;
  const vida = vidaUtil(linea, hoy);
  return {
    ultimo,
    resultado: conResultado?.resultado || null,
    resultado_fecha: conResultado?.fecha || null,
    resultado_tipo: conResultado?.tipo || null,
    proxima_fecha: proxima,
    sin_eventos: !ultimo,
    vencida: !!(proxima && hoy && hoy > proxima),
    ...vida,
    cambio_vencido: vida.vencido,
  };
}

const num = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));

// Normaliza una fila de cables_v_lineas_estado a un mismo objeto para la pantalla.
export function normalizarFilaEstado(f, hoy) {
  const vida = vidaUtil(f, hoy);
  const restante = num(f.vida_restante_anios) ?? vida.restante;
  const sinEventos = typeof f.sin_eventos === 'boolean' ? f.sin_eventos : !f.ultimo_evento_fecha;
  const proxima = f.proxima_fecha || null;
  return {
    ...f,
    instalacion: instalacionDe(f),
    resultado: RESULTADOS.includes(f.resultado) ? f.resultado : null,
    proxima_fecha: proxima,
    sin_eventos: sinEventos,
    vencida: typeof f.vencida === 'boolean' ? f.vencida : !!(proxima && hoy && hoy > proxima),
    edad_anios: num(f.edad_anios) ?? vida.edad,
    vida_restante_anios: restante,
    cambio_vencido: typeof f.cambio_vencido === 'boolean' ? f.cambio_vencido : restante !== null && restante < 0,
    menciones_30d: Number(f.menciones_30d ?? 0) || 0,
  };
}

// ── Lista ──
export const FILTROS = [
  { id: '', label: 'Todos' },
  ...RESULTADOS.map((r) => ({ id: r, label: RESULTADO_LABEL[r] })),
  { id: 'vencida', label: 'Revisión pendiente' },
  { id: 'cambio_vencido', label: 'Cambio vencido' },
  { id: 'sin_resultado', label: 'Sin inspección anual' },
];

export function filtrarLineas(lista, { instalacion, filtro = '', q = '', incluirBajas = false } = {}) {
  const t = normalizarTexto(q);
  return (lista || []).filter((l) => {
    if (instalacion && instalacionDe(l) !== instalacion) return false;
    if (!incluirBajas && l.activo === false) return false;
    if (filtro === 'vencida' && !l.vencida) return false;
    if (filtro === 'cambio_vencido' && !l.cambio_vencido) return false;
    if (filtro === 'sin_resultado' && l.resultado) return false;
    if (RESULTADOS.includes(filtro) && l.resultado !== filtro) return false;
    if (t) {
      const texto = normalizarTexto([l.codigo, l.nombre, l.plataforma_desde, l.plataforma_hasta, l.material, l.fabricante]
        .filter(Boolean).join(' '));
      if (!texto.includes(t)) return false;
    }
    return true;
  }).sort(compararCodigo);
}

// Cuántos cables activos caen en cada filtro (los chips). '' = todos.
export function conteoFiltros(lista) {
  const activos = (lista || []).filter((l) => l.activo !== false);
  const out = { '': activos.length, vencida: 0, cambio_vencido: 0, sin_resultado: 0 };
  for (const r of RESULTADOS) out[r] = 0;
  for (const l of activos) {
    if (RESULTADOS.includes(l.resultado)) out[l.resultado] += 1;
    else out.sin_resultado += 1;
    if (l.vencida) out.vencida += 1;
    if (l.cambio_vencido) out.cambio_vencido += 1;
  }
  return out;
}

export function resumenLineas(lista) {
  const activos = (lista || []).filter((l) => l.activo !== false);
  return {
    activos: activos.length,
    observados: activos.filter((l) => esResultadoMalo(l.resultado)).length,
    noApto: activos.filter((l) => l.resultado === 'no_apto').length,
    vencidas: activos.filter((l) => l.vencida).length,
    cambioVencido: activos.filter((l) => l.cambio_vencido).length,
    sinResultado: activos.filter((l) => !l.resultado).length,
  };
}

// ── Ficha ──
const texto = (v) => {
  const s = String(v ?? '').trim();
  return s || null;
};
const numero = (v) => {
  if (v === null || v === undefined || String(v).trim() === '') return null;
  const n = Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : NaN;
};

export const CAMPOS_FICHA = [
  { k: 'codigo', label: 'Código' },
  { k: 'nombre', label: 'Nombre' },
  { k: 'instalacion', label: 'Instalación' },
  { k: 'plataforma_desde', label: 'Plataforma de salida' },
  { k: 'plataforma_hasta', label: 'Plataforma de llegada' },
  { k: 'arbol_desde_id', label: 'Árbol de salida' },
  { k: 'arbol_hasta_id', label: 'Árbol de llegada' },
  { k: 'longitud_m', label: 'Longitud (m)' },
  { k: 'diametro_mm', label: 'Diámetro (mm)' },
  { k: 'material', label: 'Material' },
  { k: 'construccion', label: 'Construcción' },
  { k: 'carga_rotura_kn', label: 'Carga de rotura (kN)' },
  { k: 'tension_diseno', label: 'Tensión de diseño' },
  { k: 'flecha_diseno', label: 'Flecha de diseño' },
  { k: 'freno', label: 'Freno' },
  { k: 'fabricante', label: 'Fabricante' },
  { k: 'proveedor', label: 'Proveedor' },
  { k: 'fecha_instalacion', label: 'Fecha de instalación' },
  { k: 'vida_util_anios', label: 'Vida útil (años)' },
  { k: 'foto_path', label: 'Foto' },
  { k: 'notas', label: 'Notas' },
  { k: 'activo', label: 'Activo' },
  { k: 'baja_motivo', label: 'Motivo de baja' },
];

export const historialLinea = (historial, actual) => historialFicha(historial, actual, CAMPOS_FICHA);

// Valor legible de un campo en el historial. codigos: { arbolId: 'A1' }.
export const valorCampo = (k, v, codigos = {}) => {
  if (v === null || v === undefined || v === '') return 'vacío';
  if (k === 'activo') return v ? 'sí' : 'no';
  if (k === 'instalacion') return etiquetaInstalacion(v);
  if (k === 'foto_path') return 'foto nueva';
  if (k === 'arbol_desde_id' || k === 'arbol_hasta_id') return codigos[v] || 'otro árbol';
  return String(v);
};

// Formulario (texto) a fila para la base. Devuelve { datos, errores }.
export function prepararLinea(form, hoy) {
  const errores = {};
  const codigo = texto(String(form.codigo ?? '').replace(/\s+/g, ''))?.toUpperCase() || null;
  if (!codigo) errores.codigo = 'Escriba el código del cable (por ejemplo C1).';
  else if (codigo.length > 20) errores.codigo = 'Código demasiado largo.';
  const fecha = texto(form.fecha_instalacion);
  if (fecha && !/^\d{4}-\d{2}-\d{2}$/.test(fecha)) errores.fecha_instalacion = 'Fecha no válida.';
  else if (fecha && hoy && fecha > hoy) errores.fecha_instalacion = 'La fecha no puede ser futura.';
  const desde = texto(form.arbol_desde_id);
  const hasta = texto(form.arbol_hasta_id);
  if (desde && hasta && desde === hasta) errores.arbol_hasta_id = 'Debe ser otro árbol que el de salida.';
  const datos = {
    codigo,
    instalacion: instalacionValida(form.instalacion) ? form.instalacion : 'canopy',
    nombre: texto(form.nombre),
    plataforma_desde: texto(form.plataforma_desde),
    plataforma_hasta: texto(form.plataforma_hasta),
    arbol_desde_id: desde,
    arbol_hasta_id: hasta,
    material: texto(form.material),
    construccion: texto(form.construccion),
    fabricante: texto(form.fabricante),
    proveedor: texto(form.proveedor),
    fecha_instalacion: fecha,
    tension_diseno: texto(form.tension_diseno),
    flecha_diseno: texto(form.flecha_diseno),
    freno: texto(form.freno),
    notas: texto(form.notas),
  };
  const rangos = { longitud_m: 5000, diametro_mm: 100, vida_util_anios: 100, carga_rotura_kn: 5000 };
  for (const [k, max] of Object.entries(rangos)) {
    const n = numero(form[k]);
    if (Number.isNaN(n)) errores[k] = 'Escriba un número.';
    else if (n !== null && (n <= 0 || n > max)) errores[k] = `Debe ser mayor que 0 y hasta ${max}.`;
    datos[k] = Number.isNaN(n) ? null : n;
  }
  return { datos, errores };
}

export const lineaAFormulario = (l = {}) => ({
  codigo: l.codigo || '', instalacion: l.instalacion || 'canopy', nombre: l.nombre || '',
  plataforma_desde: l.plataforma_desde || '', plataforma_hasta: l.plataforma_hasta || '',
  arbol_desde_id: l.arbol_desde_id || '', arbol_hasta_id: l.arbol_hasta_id || '',
  longitud_m: l.longitud_m ?? '', diametro_mm: l.diametro_mm ?? '', material: l.material || '',
  construccion: l.construccion || '', fabricante: l.fabricante || '', proveedor: l.proveedor || '',
  fecha_instalacion: l.fecha_instalacion || '', tension_diseno: l.tension_diseno || '',
  flecha_diseno: l.flecha_diseno || '', vida_util_anios: l.vida_util_anios ?? '',
  carga_rotura_kn: l.carga_rotura_kn ?? '', freno: l.freno || '', notas: l.notas || '',
});

// Solo los campos que cambiaron (para no tocar lo que no se editó).
export function cambiosLinea(anterior, datos) {
  const out = {};
  for (const [k, v] of Object.entries(datos)) {
    const a = anterior?.[k] ?? null;
    if (String(a ?? '') !== String(v ?? '')) out[k] = v;
  }
  return out;
}

// ── Evento ──
export const eventoVacio = (hoy) => ({
  fecha: hoy || '', tipo: 'inspeccion_anual', responsable_nombre: '', responsable_profesion: '', empresa: '',
  certificado: '', resultado: '', detalle: '', recomendaciones: '', acciones: '', proxima_fecha: '',
});

export const resultadoObligatorio = (tipo) => tipo === 'inspeccion_anual';

export function prepararEvento(form, hoy) {
  const errores = {};
  if (!/^\d{4}-\d{2}-\d{2}$/.test(form.fecha || '')) errores.fecha = 'Elija la fecha del evento.';
  else if (hoy && form.fecha > hoy) errores.fecha = 'La fecha no puede ser futura.';
  if (!TIPOS_EVENTO.some((t) => t.id === form.tipo)) errores.tipo = 'Elija el tipo de evento.';
  const nombre = texto(form.responsable_nombre);
  if (!nombre || nombre.length < 2) errores.responsable_nombre = 'Escriba el nombre del responsable.';
  else if (nombre.length > 120) errores.responsable_nombre = 'Máximo 120 caracteres.';
  const resultado = RESULTADOS.includes(form.resultado) ? form.resultado : null;
  if (resultadoObligatorio(form.tipo) && !resultado) errores.resultado = 'En una inspección anual, elija el resultado.';
  if (form.proxima_fecha) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.proxima_fecha)) errores.proxima_fecha = 'Fecha no válida.';
    else if (form.fecha && form.proxima_fecha <= form.fecha) errores.proxima_fecha = 'Debe ser posterior al evento.';
  }
  const datos = {
    fecha: form.fecha,
    tipo: form.tipo,
    responsable_nombre: nombre,
    responsable_profesion: texto(form.responsable_profesion),
    empresa: texto(form.empresa),
    certificado: texto(form.certificado),
    resultado,
    detalle: texto(form.detalle),
    recomendaciones: texto(form.recomendaciones),
    acciones: texto(form.acciones),
    proxima_fecha: form.proxima_fecha || null,
  };
  return { datos, errores };
}

// Al registrar un cambio de cable se ofrece llevar su fecha a la ficha.
export const ofrecerFechaInstalacion = (evento, linea) => !!(evento?.tipo === 'cambio_cable' && evento.fecha
  && evento.fecha !== linea?.fecha_instalacion);

// Peor resultado de un grupo (las anuladas no cuentan).
export const resultadoPeor = (evs) => (evs || [])
  .filter((e) => e && !e.anulada && RESULTADO_ORDEN[e.resultado])
  .reduce((max, e) => (!max || RESULTADO_ORDEN[e.resultado] > RESULTADO_ORDEN[max] ? e.resultado : max), null);

// ── Inspección diaria ──
// Ids de cables que una inspección menciona (detalles.<ítem>.lineas).
export function lineasDeInspeccion(r) {
  const out = [];
  for (const d of Object.values(r?.detalles || {})) {
    for (const id of Array.isArray(d?.lineas) ? d.lineas : []) {
      if (idLineaValido(id) && !out.includes(id)) out.push(id);
    }
  }
  return out;
}

// Menciones de un cable en inspecciones (vigentes), más nuevas primero.
export function mencionesDeLinea(inspecciones, lineaId) {
  const out = [];
  const vistas = new Set();
  for (const r of inspecciones || []) {
    if (!r || r.anulada) continue;
    for (const [key, d] of Object.entries(r.detalles || {})) {
      if (!Array.isArray(d?.lineas) || !d.lineas.includes(lineaId)) continue;
      const clave = `${r.id}:${key}`;
      if (vistas.has(clave)) continue;
      vistas.add(clave);
      out.push({
        inspeccion_id: r.id, fecha: r.fecha, guia: r.guia_nombre || '', itemKey: key,
        estado: r.resultados?.[key] || null, nota: d.nota || '',
      });
    }
  }
  return out.sort((a, b) => (b.fecha || '').localeCompare(a.fecha || '') || String(b.inspeccion_id).localeCompare(String(a.inspeccion_id)));
}

// Admin y editor registran cables, editan fichas y cargan eventos.
export const puedeEditarCables = (role) => role === 'admin' || role === 'editor';

// ── Reporte ──
// Cables de una instalación para el reporte (PDF y CSV): estado de la vista
// más las menciones de la inspección diaria DENTRO del período. null si no se
// pudo consultar (el reporte lo dice en vez de omitir la sección).
export function lineasParaReporte({ lineas, inspecciones, instalacion, hoy }) {
  if (!Array.isArray(lineas)) return null;
  const lista = filtrarLineas(lineas.map((l) => normalizarFilaEstado(l, hoy)), { instalacion });
  return lista.map((l) => {
    const m = mencionesDeLinea(inspecciones, l.id);
    const porInspeccion = new Set(m.map((x) => x.inspeccion_id));
    return {
      ...l,
      menciones: porInspeccion.size,
      mencionesTexto: m.map((x) => `${x.fecha} ${x.estado || ''}`.trim()).join(' / '),
    };
  });
}
